// src/pages/conocimiento/components/ArticuloFormModal.tsx
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { useCategorias, useCrearArticulo, useActualizarArticulo } from '@/hooks/queries/useConocimiento';
import {
  TIPO_ARTICULO_LABELS,
  AMBITO_ARTICULO_LABELS,
  VISIBILIDAD_ARTICULO_LABELS,
} from '@/types';
import type {
  Articulo,
  ArticuloCreate,
  TipoArticulo,
  AmbitoArticulo,
  VisibilidadArticulo,
} from '@/types';

interface ArticuloFormModalProps {
  open: boolean;
  onClose: () => void;
  articulo?: Articulo;
}

const tipoOptions = Object.entries(TIPO_ARTICULO_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const ambitoOptions = Object.entries(AMBITO_ARTICULO_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const visibilidadOptions = Object.entries(VISIBILIDAD_ARTICULO_LABELS).map(([value, label]) => ({
  value,
  label,
}));

export function ArticuloFormModal({ open, onClose, articulo }: ArticuloFormModalProps) {
  const { data: categorias } = useCategorias();
  const crearArticulo = useCrearArticulo();
  const actualizarArticulo = useActualizarArticulo();

  const [formData, setFormData] = useState({
    titulo: '',
    tipo: 'TIP' as TipoArticulo,
    ambito: 'SOPORTE_CLIENTE' as AmbitoArticulo,
    visibilidad: 'INTERNO' as VisibilidadArticulo,
    contenido: '',
    resumen: '',
    categoria_id: '',
    motivo_cambio: '',
  });

  useEffect(() => {
    if (articulo) {
      setFormData({
        titulo: articulo.titulo,
        tipo: articulo.tipo,
        ambito: articulo.ambito,
        visibilidad: articulo.visibilidad,
        contenido: articulo.contenido || '',
        resumen: articulo.resumen || '',
        categoria_id: articulo.categoria_id || '',
        motivo_cambio: '',
      });
    } else {
      setFormData({
        titulo: '',
        tipo: 'TIP',
        ambito: 'SOPORTE_CLIENTE',
        visibilidad: 'INTERNO',
        contenido: '',
        resumen: '',
        categoria_id: '',
        motivo_cambio: '',
      });
    }
  }, [articulo, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (articulo) {
        await actualizarArticulo.mutateAsync({
          id: articulo.id,
          data: {
            titulo: formData.titulo,
            tipo: formData.tipo,
            ambito: formData.ambito,
            visibilidad: formData.visibilidad,
            contenido: formData.contenido,
            resumen: formData.resumen || undefined,
            categoria_id: formData.categoria_id || undefined,
            motivo_cambio: formData.motivo_cambio || undefined,
          },
        });
      } else {
        const createData: ArticuloCreate = {
          titulo: formData.titulo,
          tipo: formData.tipo,
          ambito: formData.ambito,
          visibilidad: formData.visibilidad,
          contenido: formData.contenido,
          resumen: formData.resumen || undefined,
          categoria_id: formData.categoria_id || undefined,
        };
        await crearArticulo.mutateAsync(createData);
      }
      onClose();
    } catch (error) {
      console.error('Error al guardar artículo:', error);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isLoading = crearArticulo.isPending || actualizarArticulo.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{articulo ? 'Editar Artículo' : 'Nuevo Artículo'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="titulo">
              Título <span className="text-red-500">*</span>
            </Label>
            <Input
              id="titulo"
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              required
              placeholder="Título del artículo"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select
                value={formData.tipo}
                onValueChange={(value) => setFormData({ ...formData, tipo: value as TipoArticulo })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tipoOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ámbito</Label>
              <Select
                value={formData.ambito}
                onValueChange={(value) => setFormData({ ...formData, ambito: value as AmbitoArticulo })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ambitoOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Visibilidad</Label>
              <Select
                value={formData.visibilidad}
                onValueChange={(value) => setFormData({ ...formData, visibilidad: value as VisibilidadArticulo })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {visibilidadOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Categoría</Label>
            <Select
              value={formData.categoria_id || 'none'}
              onValueChange={(value) => setFormData({ ...formData, categoria_id: value === 'none' ? '' : value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccione una categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin categoría</SelectItem>
                {categorias?.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resumen">Resumen</Label>
            <Textarea
              id="resumen"
              value={formData.resumen}
              onChange={(e) => setFormData({ ...formData, resumen: e.target.value })}
              placeholder="Breve descripción del artículo"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="contenido">
              Contenido <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="contenido"
              value={formData.contenido}
              onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
              placeholder="Contenido del artículo (soporta Markdown)"
              rows={10}
              required
            />
          </div>

          {articulo && (
            <div className="space-y-2">
              <Label htmlFor="motivo_cambio">Motivo del cambio</Label>
              <Input
                id="motivo_cambio"
                value={formData.motivo_cambio}
                onChange={(e) => setFormData({ ...formData, motivo_cambio: e.target.value })}
                placeholder="Describe los cambios realizados"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={isLoading}>
              {articulo ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
